import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from "react-router-dom";
import apiConfig from '../../config/apiConfig';
import {
  AllCommunityModule,
  ModuleRegistry,
  themeAlpine,
  themeBalham
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useQuery } from '@tanstack/react-query';
import { LoginContext } from '../../context/LoginContext';
import { useRaspStore } from '../../store/raspStore';
import DriveModel from '../../models/DriveModel';
import { apiFetch } from '../../apis/apiFetch';

ModuleRegistry.registerModules([AllCommunityModule]);

export interface ColumnDef {
  field: string;
  headerName: string;
  editable: boolean;
  resizable: boolean;
  sortable: boolean;
  filter: boolean;
  cellRenderer?: (params: any) => React.ReactNode;
}

const ActionCellRenderer = (props: any) => {
  const handleEdit = () => {
    props.context.handleUpdate(props.data.id);
  };

  return (
    <button onClick={handleEdit} className="btn btn-primary">
      Edit
    </button>
  );
};

export type ResourceMetaData = {
  "resource": string,
  "fieldValues": any[]
}

const UpdateDrive = () => {
  const { user } = useContext(LoginContext);
  const [rowData, setRowData] = useState<any[]>([]);
  const [colDef1, setColDef1] = useState<any[]>([]);
  const [resMetaData, setResMetaData] = useState<ResourceMetaData[]>([]);
  const [fields, setFields] = useState<any[]>([]);
  const [requiredFields, setRequiredFields] = useState<string[]>([]);
  const [fetchData, setFetchedData] = useState<any[]>([]);
  const [editedData, setEditedData] = useState<any>({});
  const [showToast, setShowToast] = useState<any>(false);
  const navigate = useNavigate();
  const apiUrl = `${apiConfig.getResourceUrl('drive')}?`
  const metadataUrl = `${apiConfig.getResourceMetaDataUrl('Drive')}?`
  const BaseUrl = `${apiConfig.API_BASE_URL}`;
  const regex = /^(g_|archived|extra_data)/;

  const getUserAllData = useRaspStore((s) => s.getUserAllData);

  const [currentUrl, setCurrentUrl] = useState('');

  const { data: dataRes, isLoading: isLoadingDataRes, error: errorDataRes } = useQuery({
    queryKey: ['resourceData', 'driveUpdate'],
    queryFn: async () => {
      const params = new URLSearchParams();

      const queryId: any = "GET_ALL";
      params.append("queryId", queryId);
      const response = await apiFetch(
        `${apiConfig.getResourceUrl('drive')}?` + params.toString(),
        {
          method: "GET",
        }
      );

      if (!response.ok) {
        throw new Error("Error: " + response.status);
      }

      const data = await response.json();
      setFetchedData(data.resource || []);
      const initialEditedData = fetchData.reduce((acc: any, item: any) => {
        acc[item.id] = { ...item };
        return acc;
      }, {});
      return data;
    },
  })

  const { data: dataResMeta, isLoading: isLoadingDataResMeta, error: errorDataResMeta } = useQuery({
    queryKey: ['resourceMetaData', 'driveUpdate'],
    queryFn: async () => {
      const response = await apiFetch(
        `${apiConfig.getResourceMetaDataUrl('drive')}?`,
        {
          method: "GET",
        }
      );

      if (!response.ok) {
        throw new Error("Error: " + response.status);
      }

      const data = await response.json();
      setResMetaData(data);
      setFields(data[0]?.fieldValues || []);
      const required = data[0]?.fieldValues
        .filter((field: any) => !regex.test(field.name))
        .map((field: any) => field.name);
      setRequiredFields(required || []);
      return data;
    },
  });

  const handleEdit = (id: any, field: string, value: any) => {
    setEditedData((prevData: any) => ({
      ...prevData,
      [id]: {
        ...(prevData[id] || {}),
        [field]: value,
      },
    }));
  };

  const handleUpdate = async (id: any) => {
    navigate(`/edit/drive/${id}`);
  };

  useEffect(() => {
    if (dataRes && dataRes.resource) {
      let dataToStore = { resource: 'drive', records: dataRes.resource }
      useRaspStore.getState().initializeStore(user?.userId || "", dataToStore);
      const modelObjects = dataRes.resource.map((obj: any) => DriveModel.fromJson(obj));
      const jsonObjects = modelObjects.map((model: any) => model.toJson());
      setRowData(jsonObjects);
    }
  }, [dataRes]);

  useEffect(() => {
    const fields = requiredFields.filter((field: any) => field !== 'id') || [];

    const columns = fields.map((field: any) => {
      const baseCol = {
        field: field,
        headerName:
          field === 'startdate' ? 'Start Date' :
          field === 'enddate' ? 'End Date' :
          field === 'editstartdate' ? 'JEE Advanced Edit Start Date' :   // ✅ ADDED
          field === 'editenddate' ? 'JEE Advanced Edit End Date' :       // ✅ ADDED
          field === 'alloweditingenddate' ? 'Editing End Date' :
          field === 'jee_editing_end_date' ? 'JEE Editing End Date' :
          field === 'drive_id' ? 'Drive ID' :
          field === 'drivetype' ? 'Drive Type' :
          field.charAt(0).toUpperCase() + field.slice(1),
        editable: false,
        resizable: true,
        sortable: true,
        filter: true
      };

      if (
        field === 'startdate' ||
        field === 'enddate' ||
        field === 'editstartdate' ||   // ✅ ADDED
        field === 'editenddate' ||        // ✅ ADDED
        field === 'alloweditingenddate' ||
        field === 'jee_editing_end_date'
      ) {
        return {
          ...baseCol,
          valueFormatter: (params: any) => {
            if (!params.value) return "";
            const date = new Date(params.value);
            if (isNaN(date.getTime())) return params.value;
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            return `${day}-${month}-${year}`;
          }
        };
      }

      return baseCol;
    });

    columns.push({
      headerName: 'Action',
      field: 'Action',
      cellRenderer: ActionCellRenderer,
      editable: false,
      resizable: true,
      sortable: false,
      filter: false,
      width: 120
    } as ColumnDef)

    setColDef1(columns);
  }, [requiredFields]);

  const defaultColDef = {
    flex: 1,
    minWidth: 100,
    editable: false,
  };

  return (
    <>
      <div className=" mt-4">
        <div>
          {rowData.length === 0 && colDef1.length === 0 ? (
            <div className="text-muted">No data available. Please add a resource attribute.</div>
          ) : (
            <div className="ag-theme-alpine border rounded" style={{ height: 500, width: '100%' }}>
              <AgGridReact
                rowData={rowData}
                columnDefs={colDef1}
                defaultColDef={defaultColDef}
                pagination={true}
                paginationPageSize={10}
                animateRows={true}
                rowSelection="multiple"
                context={{
                  handleUpdate: handleUpdate
                }}
              />
            </div>
          )}
        </div>
      </div>

      {
        showToast && (
          <div
            className="toast-container position-fixed top-20 start-50 translate-middle p-3"
            style={{ zIndex: 1550 }}
          >
            <div className="toast show" role="alert" aria-live="assertive" aria-atomic="true">
              <div className="toast-header">
                <strong className="me-auto">Success</strong>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="toast"
                  aria-label="Close"
                  onClick={() => setShowToast(false)}
                ></button>
              </div>
              <div className="toast-body text-success text-center">Created successfully!</div>
            </div>
          </div>
        )
      }
    </>
  )
};

export default UpdateDrive;